<template>
    <div>
        <!-- No empresa selected message -->
        <v-alert v-if="!empresa" type="info" dense class="mb-4">
            {{ $t('ticket.createDialog.equipamentos.noEmpresaSelected') }}
        </v-alert>

        <!-- Service uses neither vehicle, equipment nor kit -->
        <v-alert v-else-if="empresa && !usaVeiculo && !usaEquipamento && !usaKit" type="info" dense class="mb-4">
            {{ $t('ticket.createDialog.equipamentos.serviceUsesNeitherVehicleNorEquipment') }}
        </v-alert>
        
        <!-- Vehicle-Equipment Pairs Section (show when service uses vehicle, equipment or kit) -->
        <div v-else-if="empresa && (usaVeiculo || usaEquipamento || usaKit)">
            <!-- Dynamic list of vehicle-equipment pairs -->
            <div v-for="(pair, index) in vehicleEquipmentPairs" :key="index" class="mb-4">
                <v-row align="center">
                    <v-col v-if="usaEquipamento || usaKit" :cols="usaVeiculo ? 12 : 12" :sm="usaVeiculo ? 5 : 10" :md="usaVeiculo ? 5 : 10" class="d-flex align-center">
                        <!-- Equipment selection (multiple) -->
                        <v-autocomplete
                            :value="pair.equipamentos"
                            @input="updatePairEquipamentos(index, $event)"
                            :items="getEquipamentosForRow(index)"
                            item-text="displayText"
                            item-value="value"
                            :label="$t('ticket.createDialog.equipamentos.selectEquipamento')"
                            multiple
                            chips
                            clearable
                            :loading="loadingEquipamentos"
                            :disabled="loadingEquipamentos"
                            class="flex-grow-1"
                        >
                            <template v-slot:append-outer>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            small
                                            v-bind="attrs"
                                            v-on="on"
                                            color="primary"
                                            :disabled="loadingEquipamentos"
                                            @click="openEquipmentQRScanner(index)"
                                        >
                                            <v-icon small>mdi-qrcode-scan</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ $t('ticket.createDialog.equipamentos.scanQR') || 'Ler QR Code do equipamento' }}</span>
                                </v-tooltip>
                            </template>
                            <template v-slot:selection="{ item, index: chipIndex }">
                                <v-chip
                                    v-if="chipIndex < pair.equipamentos.length"
                                    small
                                    class="mr-2"
                                >
                                    {{ (item && item.displayText) ? item.displayText : getEquipamentoDisplayTextById(item && typeof item === 'object' ? (item.value !== undefined ? item.value : item.id_equipamento) : item) }}
                                </v-chip>
                                <span
                                    v-if="chipIndex === pair.equipamentos.length - 1"
                                    class="grey--text text-caption align-self-center"
                                >
                                    ({{ pair.equipamentos.length }})
                                </span>
                            </template>
                            <template v-slot:no-data>
                                <div class="pa-2">
                                    {{ $t('ticket.createDialog.equipamentos.noEquipamentos') }}
                                </div>
                            </template>
                        </v-autocomplete>
                        <!-- Filter equipments by selected vehicle (only when service uses both) -->
                        <v-tooltip v-if="usaVeiculo" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    small
                                    v-bind="attrs"
                                    v-on="on"
                                    :disabled="!pair.id_veiculo || loadingEquipamentos"
                                    :color="isFilterByVehicleActive(index) ? 'primary' : ''"
                                    @click="toggleFilterByVehicle(index)"
                                >
                                    <v-icon>{{ isFilterByVehicleActive(index) ? 'mdi-filter' : 'mdi-filter-outline' }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ isFilterByVehicleActive(index) ? $t('ticket.createDialog.equipamentos.filterByVehicleActive') : $t('ticket.createDialog.equipamentos.filterByVehicle') }}</span>
                        </v-tooltip>
                    </v-col>
                    <v-col v-if="usaVeiculo" cols="12" :sm="(usaEquipamento || usaKit) ? 5 : 10" :md="(usaEquipamento || usaKit) ? 5 : 10" class="d-flex align-center">
                        <!-- Vehicle selection (single) -->
                        <v-autocomplete
                            :value="pair.id_veiculo"
                            @input="updatePairVeiculo(index, $event)"
                            :items="veiculosFiltradosPorEmpresa"
                            item-text="displayText"
                            item-value="id_veiculo"
                            :label="$t('ticket.createDialog.equipamentos.selectVeiculo')"
                            clearable
                            :loading="loadingVeiculos"
                            :disabled="loadingVeiculos"
                            class="flex-grow-1"
                        >
                            <template v-slot:no-data>
                                <div class="pa-2">
                                    {{ $t('ticket.createDialog.equipamentos.noVeiculos') }}
                                </div>
                            </template>
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" :sm="usaVeiculo && (usaEquipamento || usaKit) ? 2 : 2" :md="usaVeiculo && (usaEquipamento || usaKit) ? 2 : 2" class="d-flex align-center justify-center">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    small
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="duplicatePair(index)"
                                >
                                    <v-icon small>mdi-content-copy</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('ticket.createDialog.equipamentos.duplicateRow') }}</span>
                        </v-tooltip>
                        <v-btn 
                            icon 
                            small 
                            @click="removePair(index)"
                            :disabled="vehicleEquipmentPairs.length === 1"
                        >
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
            
            <!-- Add row and CSV buttons (upload + help, same style as CRUDEquipamentos) -->
            <div class="d-flex flex-wrap align-center mt-2" style="gap: 8px;">
                <v-btn 
                    color="primary" 
                    @click="$emit('add-pair')"
                >
                    <v-icon left>mdi-plus</v-icon>
                    {{ addPairButtonText }}
                </v-btn>
                <template v-if="usaVeiculo">
                    <input
                        ref="csvFileInput"
                        type="file"
                        accept=".csv"
                        class="d-none"
                        @change="onCsvFileSelected"
                    />
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                dark
                                color="primary"
                                small
                                class="mb-2 mr-2"
                                @click="$refs.csvFileInput && $refs.csvFileInput.click()"
                                :disabled="loadingVeiculos || !veiculosFiltradosPorEmpresa.length"
                            >
                                <v-icon>mdi-file-upload</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('ticket.createDialog.equipamentos.uploadCsv') }}</span>
                    </v-tooltip>
                    <HelpDialogCSV
                        :fields="helpCsvFields"
                        model="ticket.createDialog.equipamentos.helpCsvModel"
                        :data="[]"
                        size="s"
                    />
                </template>
            </div>
            <v-alert v-if="csvMessage" v-model="showCsvAlert" :type="csvMessageType" dense class="mt-2" dismissible>
                {{ csvMessage }}
            </v-alert>

            <!-- Result dialog: what was extracted from the CSV; vehicles are added only on Save -->
            <ResultDialogCSV
                :token="token || ''"
                :show="showCsvResultDialog"
                :headers="csvResultHeaders"
                :result="csvResult"
                @close="onCsvResultClose"
                @save="onCsvResultSave"
            />
        </div>

        <!-- Equipment select QR Scanner Dialog (like CRUDEquipamentos) -->
        <v-dialog v-model="equipmentQrScannerDialog" max-width="800" persistent>
            <v-card>
                <v-toolbar dark color="primary">
                    <v-toolbar-title>{{ $t('equipment.qrScanner.title') || 'Ler QR Code - Equipamento' }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="closeEquipmentQRScanner">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <v-container>
                        <v-alert v-if="equipmentQrScannerError" type="error" dense class="mb-4">
                            {{ equipmentQrScannerError }}
                        </v-alert>
                        <v-alert type="info" dense class="mb-4">
                            {{ $t('equipmentChecklist.qrScannerInstructions') || $t('equipment.qrScanner.instructions') }}
                        </v-alert>
                        <div style="position: relative; width: 100%; max-width: 600px; margin: 0 auto;">
                            <qrcode-stream
                                v-if="equipmentQrScannerDialog"
                                @decode="onEquipmentQRDecode"
                                @init="onEquipmentQRInit"
                                :camera="equipmentQrCamera"
                                style="width: 100%; border-radius: 8px;"
                            ></qrcode-stream>
                        </div>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="closeEquipmentQRScanner">
                        {{ $t('button.cancel') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import HelpDialogCSV from '../Dialogs/HelpDialogCSV.vue';
import ResultDialogCSV from '../Dialogs/ResultDialogCSV.vue';
import EventBus from '../../utils/eventBus';

export default {
    name: 'TicketCreateEquipamentosFields',
    components: { QrcodeStream, HelpDialogCSV, ResultDialogCSV },
    props: {
        empresa: {
            type: [Number, String],
            default: null
        },
        /** When true, show vehicle selection (from selected service). Default true when no service. */
        usaVeiculo: {
            type: Boolean,
            default: true
        },
        /** When true, show equipment selection (from selected service). Default true when no service. */
        usaEquipamento: {
            type: Boolean,
            default: true
        },
        /** When true, show kit selection (from selected service). Default false when no service. */
        usaKit: {
            type: Boolean,
            default: false
        },
        vehicleEquipmentPairs: {
            type: Array,
            required: true
        },
        equipamentosFiltradosPorEmpresa: {
            type: Array,
            default: () => []
        },
        veiculosFiltradosPorEmpresa: {
            type: Array,
            default: () => []
        },
        loadingEquipamentos: {
            type: Boolean,
            default: false
        },
        loadingVeiculos: {
            type: Boolean,
            default: false
        },
        vehicleEquipmentPairsValid: {
            type: Boolean,
            default: true
        },
        getEquipamentoDisplayTextById: {
            type: Function,
            default: null
        },
        token: {
            type: String,
            default: ''
        }
    },
    computed: {
        addPairButtonText() {
            if (this.usaVeiculo && (this.usaEquipamento || this.usaKit)) {
                return this.$t('ticket.createDialog.equipamentos.addVehicle');
            }
            if (this.usaVeiculo) {
                return this.$t('ticket.createDialog.equipamentos.addVehicle');
            }
            return this.$t('ticket.createDialog.equipamentos.addEquipmentRow');
        },
        helpCsvFields() {
            return [
                { label: this.$t('ticket.createDialog.equipamentos.helpCsv.placa.label'), description: this.$t('ticket.createDialog.equipamentos.helpCsv.placa.description'), example: this.$t('ticket.createDialog.equipamentos.helpCsv.placa.example'), key: 'placa' }
            ];
        },
        csvResultHeaders() {
            return [
                { text: this.$t('ticket.createDialog.equipamentos.csvResult.lineNumber'), value: 'line_number' },
                { text: this.$t('ticket.createDialog.equipamentos.csvResult.placa'), value: 'placa' },
                { text: this.$t('ticket.createDialog.equipamentos.csvResult.status'), value: 'status', error: 'error_line' }
            ];
        }
    },
    data() {
        return {
            filterByVehicleForRow: [],
            csvMessage: null,
            csvMessageType: 'success',
            showCsvAlert: false,
            showCsvResultDialog: false,
            csvResult: { error: true, arqlog: '', result: [] },
            /** Pending pairs from CSV; applied only when user clicks Save in result dialog */
            pendingCsvPairs: [],
            equipmentQrScannerDialog: false,
            equipmentQrScannerError: null,
            equipmentQrCamera: 'auto',
            /** Row index for which the scanned equipment will be added */
            equipmentQrTargetPairIndex: 0
        };
    },
    watch: {
        vehicleEquipmentPairs: {
            handler(pairs) {
                while (this.filterByVehicleForRow.length < (pairs && pairs.length ? pairs.length : 0)) {
                    this.filterByVehicleForRow.push(false);
                }
            },
            immediate: true
        },
        showCsvAlert(val) {
            if (!val) this.csvMessage = null;
        }
    },
    methods: {
        getEquipamentosForRow(index) {
            const list = this.equipamentosFiltradosPorEmpresa || [];
            if (!list.length) return list;
            const pair = this.vehicleEquipmentPairs[index];
            const filterOn = this.isFilterByVehicleActive(index) && pair && pair.id_veiculo != null;
            if (!filterOn) return list;
            const idVeiculo = Number(pair.id_veiculo);
            return list.filter(e => {
                if (e.tipo === 'kit') return true; // keep kits visible even when vehicle filter is active
                return Number(e.id_veiculo) === idVeiculo; // only equipments for this vehicle
            });
        },
        isFilterByVehicleActive(index) {
            return Boolean(this.filterByVehicleForRow[index]);
        },
        toggleFilterByVehicle(index) {
            this.$set(this.filterByVehicleForRow, index, !this.filterByVehicleForRow[index]);
        },
        removePair(index) {
            this.filterByVehicleForRow.splice(index, 1);
            this.$emit('remove-pair', index);
        },
        duplicatePair(index) {
            this.filterByVehicleForRow.splice(index + 1, 0, this.filterByVehicleForRow[index]);
            this.$emit('duplicate-pair', index);
        },
        updatePairEquipamentos(index, equipamentos) {
            const updatedPairs = [...this.vehicleEquipmentPairs];
            updatedPairs[index] = { ...updatedPairs[index], equipamentos };
            this.$emit('update:pairs', updatedPairs);
        },
        updatePairVeiculo(index, id_veiculo) {
            const updatedPairs = [...this.vehicleEquipmentPairs];
            updatedPairs[index] = { ...updatedPairs[index], id_veiculo };
            this.$emit('update:pairs', updatedPairs);
        },
        getEquipamentosFromFirstFilledPair() {
            const pairs = this.vehicleEquipmentPairs || [];
            for (let i = 0; i < pairs.length; i++) {
                const eq = pairs[i].equipamentos;
                if (Array.isArray(eq) && eq.length > 0) return [...eq];
            }
            return [];
        },
        /** Build CSV string for error log download (same columns as csvResultHeaders). */
        buildCsvErrorLog(extractionResult) {
            if (!extractionResult || !extractionResult.length) return '';
            const escape = (v) => {
                const s = v == null ? '' : String(v);
                if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
                    return '"' + s.replace(/"/g, '""') + '"';
                }
                return s;
            };
            const headers = ['line_number', 'placa', 'status', 'error_line'];
            let rows = extractionResult.map(r => [
                r.line_number,
                r.placa != null ? r.placa : '',
                r.status != null ? r.status : '',
                r.error_line != null ? r.error_line : ''
            ].map(escape).join(','));
            return [headers.join(','), ...rows].join('\r\n');
        },
        parseCsvText(text) {
            let rows = [];
            let row = [];
            let cell = '';
            let inQuotes = false;
            for (let i = 0; i < text.length; i++) {
                const c = text[i];
                if (c === '"') inQuotes = !inQuotes;
                else if (inQuotes) cell += c;
                else if (c === ',' || c === ';' || c === '\t') {
                    row.push(cell.trim());
                    cell = '';
                } else if (c === '\r' && text[i + 1] === '\n') {
                    row.push(cell.trim());
                    cell = '';
                    rows.push(row);
                    row = [];
                    i++;
                } else if (c === '\n' || c === '\r') {
                    row.push(cell.trim());
                    cell = '';
                    rows.push(row);
                    row = [];
                } else cell += c;
            }
            if (cell !== '' || row.length > 0) {
                row.push(cell.trim());
                rows.push(row);
            }
            return rows;
        },
        /** CSV must have a "placa" column (or "plate"). Returns column index or 0. */
        findVehicleColumnIndex(headerRow) {
            if (!headerRow || !headerRow.length) return 0;
            const normalized = headerRow.map(h => (h || '').toLowerCase().trim());
            const names = ['placa', 'plate'];
            for (const name of names) {
                const idx = normalized.indexOf(name);
                if (idx !== -1) return idx;
            }
            return 0;
        },
        /** Resolve plate number to id_veiculo (CSV uses only placa). */
        resolveVehicleId(cellValue, veiculos) {
            if (cellValue == null || cellValue === '') return null;
            const str = String(cellValue).trim();
            const byPlaca = veiculos.find(v => (v.placa || '').toString().trim().toLowerCase() === str.toLowerCase());
            return byPlaca ? byPlaca.id_veiculo : null;
        },
        onCsvFileSelected(event) {
            const input = event && event.target;
            const file = input && input.files && input.files[0];
            this.csvMessage = null;
            this.showCsvAlert = false;
            if (!file) return;
            const veiculos = this.veiculosFiltradosPorEmpresa || [];
            if (!veiculos.length) {
                this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvErrorNoVehicles');
                this.csvMessageType = 'warning';
                this.showCsvAlert = true;
                input.value = '';
                return;
            }
            const equipamentosToCopy = this.getEquipamentosFromFirstFilledPair();
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = (e.target && e.target.result) || '';
                    let rows = this.parseCsvText(text);
                    if (!rows.length) {
                        this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvErrorEmpty');
                        this.csvMessageType = 'warning';
                        this.showCsvAlert = true;
                        input.value = '';
                        return;
                    }
                    const firstRow = rows[0];
                    const vehicleColIndex = this.findVehicleColumnIndex(firstRow);
                    const firstCell = (firstRow[vehicleColIndex] || '').toString().trim().toLowerCase();
                    const isHeaderRow = firstCell === 'placa' || firstCell === 'plate';
                    const dataRows = (isHeaderRow ? rows.slice(1) : rows).filter(r => r && r.length > 0);
                    const seenIds = new Set();
                    const newPairs = [];
                    const extractionResult = [];
                    let lineNum = isHeaderRow ? 2 : 1;
                    for (const row of dataRows) {
                        const cell = (row[vehicleColIndex] || '').toString().trim();
                        const id_veiculo = this.resolveVehicleId(cell, veiculos);
                        if (id_veiculo == null) {
                            extractionResult.push({
                                line_number: lineNum,
                                placa: cell || '—',
                                status: this.$t('ticket.createDialog.equipamentos.csvResult.statusNotFound'),
                                error_line: this.$t('ticket.createDialog.equipamentos.csvResult.placaNotFound')
                            });
                            lineNum++;
                            continue;
                        }
                        if (seenIds.has(id_veiculo)) {
                            extractionResult.push({
                                line_number: lineNum,
                                placa: cell,
                                status: this.$t('ticket.createDialog.equipamentos.csvResult.statusDuplicate'),
                                error_line: this.$t('ticket.createDialog.equipamentos.csvResult.duplicateSkipped')
                            });
                            lineNum++;
                            continue;
                        }
                        seenIds.add(id_veiculo);
                        newPairs.push({ id_veiculo, equipamentos: [...equipamentosToCopy] });
                        extractionResult.push({
                            line_number: lineNum,
                            placa: cell,
                            status: this.$t('ticket.createDialog.equipamentos.csvResult.statusAdded')
                        });
                        lineNum++;
                    }
                    const hasAnyError = extractionResult.some(r => r.error_line);
                    const logContent = hasAnyError ? this.buildCsvErrorLog(extractionResult) : '';
                    this.csvResult = {
                        error: hasAnyError,
                        arqlog: '',
                        logContent: logContent || undefined,
                        logFilename: logContent ? 'ticket_csv_errors.csv' : undefined,
                        result: extractionResult
                    };
                    this.pendingCsvPairs = hasAnyError ? [] : [...newPairs];
                    if (extractionResult.length > 0) {
                        this.showCsvResultDialog = true;
                        if (hasAnyError) {
                            this.$emit('csv-errors-open', true);
                            this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvErrorNoMatches', { skipped: extractionResult.filter(r => r.error_line).length });
                            this.csvMessageType = 'warning';
                        } else if (newPairs.length > 0) {
                            this.$emit('csv-errors-open', false);
                            this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvResult.reviewAndSave');
                            this.csvMessageType = 'info';
                        } else {
                            this.$emit('csv-errors-open', false);
                            this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvErrorNoMatches', { skipped: extractionResult.length });
                            this.csvMessageType = 'warning';
                        }
                        this.showCsvAlert = true;
                    }
                } catch (err) {
                    this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvErrorParse') || (err && err.message) || 'CSV error';
                    this.csvMessageType = 'error';
                    this.showCsvAlert = true;
                }
                input.value = '';
            };
            reader.readAsText(file, 'UTF-8');
        },
        onCsvResultSave() {
            if (this.pendingCsvPairs.length > 0) {
                const updatedPairs = [...(this.vehicleEquipmentPairs || []), ...this.pendingCsvPairs];
                this.$emit('update:pairs', updatedPairs);
                this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvSuccess', { count: this.pendingCsvPairs.length });
                this.csvMessageType = 'success';
                this.showCsvAlert = true;
            }
            this.pendingCsvPairs = [];
            this.showCsvResultDialog = false;
        },
        onCsvResultClose() {
            this.$emit('csv-errors-open', false);
            if (this.pendingCsvPairs.length > 0) {
                this.csvMessage = this.$t('ticket.createDialog.equipamentos.csvResult.importCancelled');
                this.csvMessageType = 'info';
                this.showCsvAlert = true;
            }
            this.pendingCsvPairs = [];
            this.showCsvResultDialog = false;
        },
        openEquipmentQRScanner(pairIndex) {
            this.equipmentQrTargetPairIndex = pairIndex;
            this.equipmentQrScannerError = null;
            this.equipmentQrScannerDialog = true;
        },
        closeEquipmentQRScanner() {
            this.equipmentQrScannerDialog = false;
            this.equipmentQrScannerError = null;
            this.equipmentQrCamera = 'auto';
        },
        onEquipmentQRDecode(decodedString) {
            const list = this.equipamentosFiltradosPorEmpresa || [];
            let item = null;
            try {
                const qrData = JSON.parse(decodedString);
                if (qrData.id_equipamento != null) {
                    item = list.find(e => e.id_equipamento === qrData.id_equipamento || e.id_equipamento === parseInt(qrData.id_equipamento, 10));
                }
                if (!item && qrData.id_kit != null) {
                    item = list.find(e => e.id_equipamento === qrData.id_kit || e.id_equipamento === parseInt(qrData.id_kit, 10));
                }
                if (!item && qrData.cod_equipamento) {
                    item = list.find(e => e.cod_equipamento === qrData.cod_equipamento);
                }
                if (!item && qrData.cod_kit) {
                    item = list.find(e => e.cod_equipamento === qrData.cod_kit);
                }
                if (!item && qrData.codigo_qrcode) {
                    item = list.find(e => e.codigo_qrcode === qrData.codigo_qrcode);
                }
            } catch (_) {
                item = list.find(e => e.codigo_qrcode === decodedString || e.cod_equipamento === decodedString);
            }
            if (item) {
                const value = item.value != null ? item.value : item.id_equipamento;
                const idx = this.equipmentQrTargetPairIndex;
                const pairs = [...(this.vehicleEquipmentPairs || [])];
                const current = pairs[idx] && Array.isArray(pairs[idx].equipamentos) ? pairs[idx].equipamentos : [];
                if (!current.includes(value)) {
                    pairs[idx] = { ...pairs[idx], equipamentos: [...current, value] };
                    this.$emit('update:pairs', pairs);
                }
                this.closeEquipmentQRScanner();
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('equipmentChecklist.equipmentSelected') || this.$t('equipment.qrScanner.success') || 'Equipamento adicionado.',
                    tipo: 'success'
                });
            } else {
                this.equipmentQrScannerError = this.$t('equipmentChecklist.equipmentNotFound') || this.$t('equipment.qrScanner.invalidJSON') || 'Equipamento não encontrado ou não disponível para este ticket.';
            }
        },
        onEquipmentQRInit(promise) {
            promise.then(() => {
                this.equipmentQrScannerError = null;
            }).catch((err) => {
                let msg = this.$t('equipment.qrScanner.cameraError') || 'Erro ao acessar a câmera.';
                if (err && err.name === 'NotAllowedError') msg = this.$t('equipment.qrScanner.cameraPermissionDenied') || 'Permissão de câmera negada.';
                else if (err && err.name === 'NotFoundError') msg = this.$t('equipment.qrScanner.cameraNotFound') || 'Câmera não encontrada.';
                this.equipmentQrScannerError = msg;
            });
        }
    }
};
</script>
